import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  RotateCw,
} from "lucide-react";
import { fileUrl } from "./Gallery";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHAMANIC_ALBUM_NAME = "Shamanic";

// Image with optional rotation that fits its wrapper without black bars.
const RotatedImage = ({ src, alt, rotation, imgClassName = "", eager = false }) => {
  const [nat, setNat] = useState(null);
  const r = rotation || 0;
  const isSide = r === 90 || r === 270;

  const aspectRatio = nat
    ? isSide
      ? `${nat.h} / ${nat.w}`
      : `${nat.w} / ${nat.h}`
    : undefined;

  return (
    <div className="relative w-full" style={{ aspectRatio }}>
      <img
        src={src}
        alt={alt || ""}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        onLoad={(e) =>
          setNat({
            w: e.currentTarget.naturalWidth,
            h: e.currentTarget.naturalHeight,
          })
        }
        style={
          isSide && nat
            ? {
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: `translate(-50%, -50%) rotate(${r}deg)`,
                width: `calc(100% * ${nat.w} / ${nat.h})`,
                height: `calc(100% * ${nat.h} / ${nat.w})`,
                maxWidth: "none",
              }
            : r === 180
            ? { transform: "rotate(180deg)" }
            : undefined
        }
        className={
          isSide && nat ? imgClassName : `block w-full h-auto ${imgClassName}`
        }
      />
    </div>
  );
};

export const ShamanicGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [albumId, setAlbumId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const resolveAlbum = async () => {
    try {
      const { data } = await axios.get(`${API}/albums`);
      if (Array.isArray(data)) {
        const target = data.find((a) => a.name === SHAMANIC_ALBUM_NAME);
        if (target) return target.id;
      }
      const { data: created } = await axios.post(
        `${API}/albums`,
        { name: SHAMANIC_ALBUM_NAME },
        { withCredentials: true }
      );
      return created.id;
    } catch {
      return null;
    }
  };

  const loadPhotos = async (id) => {
    if (!id) return;
    try {
      const { data } = await axios.get(`${API}/photos?limit=500&album_id=${id}`);
      setPhotos(Array.isArray(data) ? data : []);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    let active = true;
    resolveAlbum().then((id) => {
      if (!active) return;
      setAlbumId(id);
      loadPhotos(id);
    });
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => active && setIsAdmin(!!data.is_admin))
      .catch(() => active && setIsAdmin(false));
    return () => {
      active = false;
    };
  }, []);

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    let targetId = albumId || (await resolveAlbum());
    if (!targetId) {
      toast.error("No upload target available.");
      e.target.value = "";
      return;
    }
    if (!albumId) setAlbumId(targetId);
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await axios.post(`${API}/albums/${targetId}/photos`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${files.length} photo(s) added.`);
      loadPhotos(targetId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const deletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/photos/${photoId}`, { withCredentials: true });
      setPhotos((all) => all.filter((p) => p.id !== photoId));
    } catch {
      toast.error("Couldn't remove the photo.");
    }
  };

  const rotatePhoto = async (photo) => {
    const next = ((photo.rotation || 0) + 90) % 360;
    setPhotos((all) =>
      all.map((p) => (p.id === photo.id ? { ...p, rotation: next } : p))
    );
    try {
      await axios.patch(
        `${API}/photos/${photo.id}`,
        { rotation: next },
        { withCredentials: true }
      );
    } catch {
      toast.error("Couldn't rotate the photo.");
      setPhotos((all) =>
        all.map((p) =>
          p.id === photo.id ? { ...p, rotation: photo.rotation || 0 } : p
        )
      );
    }
  };

  return (
    <section
      data-testid="shamanic-gallery-section"
      className="max-w-6xl mx-auto px-6 py-14 md:py-20"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8 }}
        className="text-center mb-12"
      >
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#7A9E6D] mb-5">
          Gallery
        </p>
        <h2 className="font-heading font-light text-4xl md:text-6xl text-[#EBE4D8]">
          Echoes from the <span className="italic text-[#D6B56D]">jungle</span>
        </h2>
        <p className="text-[#A59DAB] mt-5 max-w-xl mx-auto font-light">
          Glimpses from Peru — ceremonies, the abuelos, the medicine, and the quiet moments between.
        </p>
      </motion.div>

      {isAdmin && (
        <div className="mb-10 flex justify-center">
          <label
            data-testid="shamanic-upload-label"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#D6B56D] text-[#06040A] text-sm tracking-widest uppercase font-medium hover:bg-[#F0D494] transition-all cursor-pointer"
          >
            <Upload size={16} /> {uploading ? "Uploading..." : "Add photos"}
            <input
              data-testid="shamanic-upload-input"
              type="file"
              accept="image/*"
              multiple
              onChange={uploadPhotos}
              disabled={uploading}
              className="hidden"
            />
          </label>
        </div>
      )}

      {photos.length === 0 ? (
        <p
          data-testid="shamanic-photos-empty"
          className="text-center text-[#A59DAB] font-light py-10 italic"
        >
          {isAdmin
            ? "No photos here yet — use the button above to add some."
            : "No photos here yet."}
        </p>
      ) : (
        <div
          data-testid="shamanic-masonry"
          className="columns-2 md:columns-3 lg:columns-4 gap-4 [column-fill:_balance]"
        >
          {photos.map((p, i) => (
            <motion.figure
              key={p.id}
              data-testid={`shamanic-photo-${i}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 8) * 0.03 }}
              onClick={() => setLightboxIndex(i)}
              className="group relative mb-4 break-inside-avoid rounded-2xl overflow-hidden border border-[#2C223A] hover:border-[#D6B56D]/55 transition-colors cursor-pointer bg-[#0c0a14]"
            >
              <RotatedImage
                src={fileUrl(p.image_path, 700)}
                alt={p.place || ""}
                rotation={p.rotation || 0}
                eager={i < 8}
                imgClassName="transition-transform duration-700 group-hover:scale-[1.03]"
              />

              {(p.place || p.year) && (
                <figcaption className="pointer-events-none absolute bottom-0 inset-x-0 px-4 py-3 bg-gradient-to-t from-[#06040A]/90 via-[#06040A]/35 to-transparent">
                  <p className="photo-caption-shadow text-sm md:text-base font-medium text-[#EBE4D8] leading-tight">
                    {p.place}
                    {p.place && p.year ? " · " : ""}
                    {p.year}
                  </p>
                </figcaption>
              )}

              {isAdmin && (
                <>
                  <button
                    data-testid={`shamanic-delete-${p.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm("Delete this photo?")) deletePhoto(p.id);
                    }}
                    aria-label="Delete photo"
                    className="absolute top-2 right-2 w-9 h-9 rounded-full bg-[#06040A]/75 text-[#EBE4D8] flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-red-500/85 transition-all"
                  >
                    <Trash2 size={13} />
                  </button>
                  <button
                    data-testid={`shamanic-rotate-${p.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      rotatePhoto(p);
                    }}
                    aria-label="Rotate photo"
                    className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-[#06040A]/75 text-[#EBE4D8] flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-[#D6B56D] hover:text-[#06040A] transition-all"
                  >
                    <RotateCw size={13} />
                  </button>
                </>
              )}
            </motion.figure>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && photos[lightboxIndex] && (
          <motion.div
            data-testid="shamanic-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightboxIndex(null)}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-[#05070F]/95 backdrop-blur-md p-4"
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(null);
              }}
              aria-label="Close"
              className="absolute top-5 right-5 z-[95] w-11 h-11 rounded-full bg-[#06040A]/70 text-[#EBE4D8] flex items-center justify-center hover:bg-[#D6B56D] hover:text-[#06040A] transition-all"
            >
              <X size={20} />
            </button>
            {photos.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex(
                      (x) => (x - 1 + photos.length) % photos.length
                    );
                  }}
                  aria-label="Previous"
                  className="absolute left-4 md:left-8 z-[95] w-11 h-11 rounded-full bg-[#06040A]/70 text-[#EBE4D8] flex items-center justify-center hover:bg-[#D6B56D] hover:text-[#06040A] transition-all"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex((x) => (x + 1) % photos.length);
                  }}
                  aria-label="Next"
                  className="absolute right-4 md:right-8 z-[95] w-11 h-11 rounded-full bg-[#06040A]/70 text-[#EBE4D8] flex items-center justify-center hover:bg-[#D6B56D] hover:text-[#06040A] transition-all"
                >
                  <ChevronRight size={22} />
                </button>
              </>
            )}
            <motion.div
              key={lightboxIndex}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="relative inline-block"
            >
              {(() => {
                const r = photos[lightboxIndex].rotation || 0;
                const isSide = r === 90 || r === 270;
                return (
                  <img
                    data-testid="shamanic-lightbox-image"
                    src={fileUrl(photos[lightboxIndex].image_path, 1600)}
                    alt=""
                    decoding="async"
                    style={{
                      transform: r ? `rotate(${r}deg)` : undefined,
                      maxHeight: isSide ? "92vw" : "88vh",
                      maxWidth: isSide ? "88vh" : "92vw",
                    }}
                    className="block object-contain rounded-xl"
                  />
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
